import type { DateOrRange } from '@/infrastructure/departure-date/store'
import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilterLegacy/types'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { DatesCalendar, MonthCalendar } from '@/scopes/search/components/DepartureDateFilterLegacy/Calendars'
import { Chips } from '@/scopes/search/components/DepartureDateFilterLegacy/Chips'
import { useDepartureDateFilter } from '@/scopes/search/components/DepartureDateFilterLegacy/hooks'
import Tabs from '@/scopes/search/components/DepartureDateFilterLegacy/Tabs'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBarLegacy/store'
import { ButtonLoaderIcon, SpaceBetween, tk } from '@sunwebgroup/ui-components'
import { Button } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const id = 'departure-date-filter'

const FilterWrapper = styled.div`
  min-width: 850px;
  display: flex;
  justify-content: center;
  margin-top: 0 !important;
  padding-left: ${tk('spacing_md')};
  padding-right: ${tk('spacing_md')};
`
const PopoverHeader = styled(Tabs)`
  margin-bottom: ${tk('spacing_lg')};
`

const Footer = styled.div`
  background-color: ${tk('color_semantic_bg')};
  bottom: 0;
  display: flex;
  padding-bottom: ${tk('spacing_md')};
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: 1;

  overflow-x: scroll;

  ${Button} {
    display: none;

    @media (min-width: 700px) {
      display: inline-flex;
    }
  }

  @media ${tk('mq_desktop_sm')} {
    overflow-x: initial;
    padding-top: ${tk('spacing_md')};
  }
`
const FooterButtonWrapper = styled(SpaceBetween)`
  margin-left: auto;

  button:enabled {
    span {
      margin-left: unset;
    }
  }
`

function DepartureDateFilterByView({
  options,
  className,
}: {
  options: DepartureDateFilterOptions
  className?: string
}) {
  const { view } = useDepartureDateFilter({ options })
  if (view === 'calendar')
    return <DatesCalendar className={className} options={options} />
  return <MonthCalendar className={className} options={options} />
}

export function DepartureDateFilterComponent({
  disabled,
  onConfirm,
  onReset,
  options,
}: {
  onConfirm: ({ departureDate }: { departureDate: DateOrRange }) => void
  onReset: () => void
  options: DepartureDateFilterOptions
  disabled?: boolean
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()
  const { view, departureDate, reset } = useDepartureDateStore()

  const handleConfirm = () => {
    setActiveItem(null)

    if (disabled)
      return

    if (departureDate) {
      onConfirm({ departureDate })
    }
  }

  const handleReset = () => {
    onReset()
    reset()
    setActiveItem(null)
  }

  return (
    <Filter
      id={id}
      icon="Baby"
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_date_title',
      })}
      onConfirm={handleConfirm}
    >
      <PopoverHeader options={options} />
      <FilterWrapper>
        <DepartureDateFilterByView options={options} />
      </FilterWrapper>
      <Footer>
        {view === 'calendar' && <Chips options={options} />}

        <FooterButtonWrapper direction="x" spacing={tk('spacing_xs')}>
          <Button onClick={handleReset} $type="transparentPrimary" disabled={disabled}>
            {intl.formatMessage({
              id: 'quicksearch_filter_departure_date_reset',
            })}
          </Button>
          <Button onClick={handleConfirm} disabled={disabled}>
            {disabled && <ButtonLoaderIcon />}
            {intl.formatMessage({
              id: 'quicksearch_save_button_text',
            })}
          </Button>
        </FooterButtonWrapper>
      </Footer>
    </Filter>
  )
}
